<template>
    <card v-if="simulation">

        <!-- The simulation session of another instructor must be closed first -->
        <call-for-action :vertical="!compact"
            :reduce-text="false"
            v-if="mustTerminateOtherSession"
        >
            <i class="fas fa-exclamation-triangle text-danger"></i>
            <small class="text-uppercase font-weight-bold text-danger">
                {{ $t('common.simulation') }}
            </small>
            <small class="d-block pt-3">
                {{ $t('sessions.must-terminate-other-session') }}
            </small>
        </call-for-action>

        <!-- The file must be terminated -->
        <call-for-action :vertical="!compact"
            :reduce-text="false"
            :reduce-button="false"
            :action="simulation.private ? null : $t('dashboard.close-file')"
            :handler="closeFile"
            :disabled="!canCloseFile"
            :help="simulation.assessment ? $t('reviews.learner-access-notice') : $t('dashboard.close-file-notice')"
            v-if="mustTerminateFile"
        >
            <!-- Comments -->

            <div class="mb-4 clickable" v-if="simulation.state.comment" @click="$emit('openGeneralComment')">
                <i class="fas fa-check-circle text-success"></i>
                <small class="text-uppercase font-weight-bold text-success">
                    {{ $t('dashboard.general-comment') }}
                </small>
                <small class="d-block">
                    {{ $t('dashboard.general-comment-status-done') }}
                </small>
            </div>

            <div class="mb-4 clickable" v-if="!simulation.state.comment" @click="$emit('openGeneralComment')">
                <i class="fas fa-exclamation-triangle text-danger"></i>
                <small class="text-uppercase font-weight-bold text-danger">
                    {{ $t('dashboard.general-comment') }}
                </small>
                <small class="d-block">
                    {{ $t('dashboard.general-comment-status-undone') }}
                </small>
            </div>

            <!-- Competencies -->

            <div class="mb-4 clickable" v-if="competenciesFilled" @click="$emit('openCompetencies')">
                <i class="fas fa-check-circle text-success"></i>
                <small class="text-uppercase font-weight-bold text-success">
                    {{ simulation.assessment ? $t('common.competency-domains') : $t('common.competencies') }}
                </small>
                <small class="d-block">
                    {{ simulation.assessment ? $t('dashboard.competency-domains-status-done') : $t('dashboard.competencies-status-done') }}
                </small>
            </div>

            <div class="mb-4 clickable" v-if="!competenciesFilled" @click="$emit('openCompetencies')">
                <i class="fas fa-exclamation-triangle text-danger"></i>
                <small class="text-uppercase font-weight-bold text-danger">
                    {{ simulation.assessment ? $t('common.competency-domains') : $t('common.competencies') }}
                </small>
                <small class="d-block">
                    {{ simulation.assessment ? $t('dashboard.competency-domains-status-undone') : $t('dashboard.competencies-status-undone') }}
                </small>
            </div>

            <!-- Learner access date -->

            <div class="mb--2 text-center access-date" v-if="canCloseFile && simulation.assessment && !simulation.private" >
                <date-input class="0" v-model="learnerAccessDate" :label="$t('reviews.learner-access')" ref="date">
                </date-input>
            </div>

        </call-for-action>

        <!-- The file is closed: reopen the file -->
        <call-for-action :vertical="!compact"
            :action="$t('dashboard.reopen-file')"
            :handler="reopenFile"
            :reduce-text="false"
            v-if="canReopenFile"
        >
            <span v-html="reopenNotice"></span>
        </call-for-action>

        <!-- This file has not been published yet. Propose to edit. -->
        <call-for-action :vertical="!compact"
            :action="$t('common.edit')"
            icon="fas fa-pencil-alt"
            :handler="editFile"
            v-if="canEditFile"
        >
            {{ $t('dashboard.edit-file-notice') }}
        </call-for-action>

        <!-- No session exists for this learner -->
        <call-for-action :vertical="!compact"
            v-if="noSessionExists"
        >
            {{ $t('dashboard.file-without-session') }}
        </call-for-action>

        <portal to="modals">

            <confirm-modal v-model="modals.confirmClose">
                <p>{{ $t('dashboard.close-file-confirm') }}</p>
            </confirm-modal>

            <reopen-modal v-model="modals.reopenComment"
                :endpoint="reopenEndpoint"
                @change="reopened"
            >
            </reopen-modal>

        </portal>
    </card>
</template>

<script>
    import moment from 'moment'
    import CallForAction from '../common/CallForAction'
    import ModalsController from '@trax/classes/ModalsController'
    import ReopenModal from './SimulationReopenModal'

    export default {

        components: {
            CallForAction, ReopenModal
        },

        props: {

            registration: {
                type: Object,
                required: true
            },

            simulation: {
                type: Object,
                required: true
            },

            learner: {
                type: Object,
                required: true
            },

            editPage: {
                type: Boolean,
                default: true
            },

            competenciesFilled: {
                type: Boolean,
                default: false
            },

            compact: {
                type: Boolean,
                default: false
            },
        },

        data() {
            return {
                moment,
                learnerAccessDate: null,
                modals: new ModalsController(['confirmClose', 'reopenComment']),
            }
        },

        created() {
            this.modals.init(this, 'modals')

            this.learnerAccessDate = this.simulation.state && this.simulation.state.meta && this.simulation.state.meta.learner_access_date
                ? this.simulation.state.meta.learner_access_date
                : this.moment().format('YYYY/MM/DD')
        },

        computed: {

            mustTerminateMySession() {
                return this.editPage
                    && this.simulation.session 
                    && !this.simulation.session.ended_at
                    && this.simulation.session.instructor.id == this.$auth.user.id
            },

            mustTerminateOtherSession() {
                return this.editPage
                    && this.simulation.session 
                    && !this.simulation.session.ended_at
                    && this.simulation.session.instructor.id != this.$auth.user.id
            },

            mustTerminateFile() {
                return this.editPage
                    && (this.simulation.session || this.simulation.assessment)
                    && !this.simulation.state.published
            },

            canCloseFile() {
                return !this.simulation.state.published
                    && this.simulation.state.comment
                    && this.competenciesFilled
            },

            canReopenFile() {
                return !this.editPage
                    && (this.simulation.session || this.simulation.assessment)
                    && this.simulation.state.published
            },

            canEditFile() {
                return !this.editPage
                    && (this.simulation.session || this.simulation.assessment)
                    && !this.simulation.state.published
            },

            noSessionExists() {
                return !this.editPage
                    && !this.simulation.session
                    && !this.simulation.assessment
            },

            closeEndpoint() {
                return '/astral/api/front/dashboard/registrations/'+this.registration.id+'/simulations/'+this.simulation.id+'/learners/'+this.learner.id+'/close'
            },

            reopenEndpoint() {
                return '/astral/api/front/dashboard/registrations/'+this.registration.id+'/simulations/'+this.simulation.id+'/learners/'+this.learner.id+'/reopen'
            },

            reopenNotice() {
                if (!this.simulation.assessment) {
                    return this.$t('dashboard.reopen-file-notice');
                }
                return this.$t('reviews.reopen-review-notice') + '<b>' + this.simulation.state.meta.learner_access_date + '</b>.';
            }   
        },

        methods: {

            readFile() {
                this.$router.push({
                    name: 'dashboard-learner-simulation',
                    params: {
                        learnerId: this.learner.id,
                        registrationId: this.registration.id,
                        simulationId: this.simulation.id,
                    }
                });
            },

            editFile() {
                this.$router.push({
                    name: 'dashboard-learner-simulation-edit',
                    params: {
                        learnerId: this.learner.id,
                        registrationId: this.registration.id,
                        simulationId: this.simulation.id,
                    }
                });
            },

            closeFile() {
                this.modals.open('confirmClose').then(resp => {
                    if (resp.confirmed) {
                        this.$loader.show()
                        axios.post(this.closeEndpoint, { learner_access_date: this.learnerAccessDate })
                        .then(resp => {
                            this.readFile()
                        })
                        .catch(err => {
                            if (err.response.status == 500 && err.response.data.message) {
                                this.$notify({type: 'danger', message: err.response.data.message })
                                this.readFile()
                            } else {
                                this.$auth.handleErrorVm(err, this)
                            }
                        })        
                    }
                })
            },

            reopenFile() {
                this.modals.open('reopenComment')
            },

            reopened() {
                this.$router.push({
                    name: 'dashboard-learner-simulation-edit',
                    params: {
                        learnerId: this.learner.id,
                        registrationId: this.registration.id,
                        simulationId: this.simulation.id,
                    }
                })
            }
        }
    }
</script>

<style scoped>

    .clickable {
        cursor: pointer;
    }
    .access-date >>> input {
        width: 140px;
        margin-right: auto;
        margin-left: auto;
    }

</style>
